﻿import {currentCulture, translation} from "@2d-front-monorepo/core";
import {MvvmOptionListEntry} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {useMemo} from "react";
import {DefaultParkingCategory, ParkingCategory} from "../nominalTypes/ParkingCategory";
import {TariffId} from "../nominalTypes/TariffId";
import {ZoneId} from "../nominalTypes/ZoneId";
import {Tariffs} from "../Tariffs";
import {InstallationByHostData} from "../types/InstallationByHostData";
import {ZoneOccupation} from "../types/QueryOccupationResult";
import {Zones} from "../Zones";

export interface TariffWithDescription extends MvvmOptionListEntry<TariffId> {
	occupied: number;
	total: number;
}

const MAX_OCCUPATION = 1000000;

/**
 * Capacity of the zones a tariff covers, or undefined when NONE of them appears in the occupation
 * result (unknown capacity, which must stay selectable). A zone that is present with `total: 0` is
 * a full zone - that is how the API reports one ({total: 0, occupied: 0, occupiedPercent: 100}).
 */
export function sumZoneOccupation(zoneIds: ZoneId[], occupation?: ZoneOccupation[]): { total: number; occupied: number } | undefined {
	const entries = occupation ? zoneIds.map(id => occupation.find(o => o.id === id)).filter(o => o !== undefined) : [];
	return entries.length === 0 ? undefined : entries.reduce((sum, o) => ({
		total: sum.total + o.total,
		occupied: sum.occupied + o.occupied
	}), {total: 0, occupied: 0});
}

export default function useTariffWithDescription(installation: InstallationByHostData, tariffIds: TariffId[], entityCategory: ParkingCategory, occupation?: ZoneOccupation[], skipLabelOccupation?: boolean): TariffWithDescription[] {
	return useMemo<TariffWithDescription[]>(() => Tariffs.getAll(installation, tariffIds).map(z => {
		const key = entityCategory === DefaultParkingCategory ? `tariff_type__${z.type}--hint` : `${entityCategory}__tariff_type__${z.type}--hint`;
		const zones = Zones.getAll(installation, Tariffs.getZones(installation, z.type));
		const zoneNames = currentCulture().formatCollection(zones.map(z => z.name));
		const description = translation(key, {zones: zoneNames});
		const occupationEntry = sumZoneOccupation(zones.map(zone => zone.id), occupation);

		const hasOccupation = occupationEntry != null && occupationEntry.total <= MAX_OCCUPATION;
		let label: string = PermitTranslations.tariff_list_format1;

		if (!skipLabelOccupation) {
			if (hasOccupation && occupationEntry.total < MAX_OCCUPATION) {
				label = PermitTranslations.tariff_list_format2;
			}
		}

		const occupied = hasOccupation ? occupationEntry.occupied ?? 0 : 0;
		const total = hasOccupation ? occupationEntry.total ?? 0 : 0;

		return {
			label: translation(label, {name: z.title, occupied: occupied, total: total}),
			value: z.type,
			description: key === description || description === "-" ? undefined : description,
			descriptionOpen: tariffIds.length === 1,
			// Disabled with capacity data only: zones missing from the occupation result and
			// unlimited tariffs (total > MAX_OCCUPATION) stay selectable. A zone that IS in the
			// result with total 0 is full (the API reports a full zone as {0, 0, 100%}).
			disabled: hasOccupation && occupied >= total,
			occupied,
			total
		};
	}), [installation, tariffIds, entityCategory, occupation]);
}
